import { useCallback, useEffect, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { InsightsService } from '../../../services/insights'
import { cancelNamedAxiosRequest } from '../../../config/axios-setup'
import { DashboardTemplate, TEMPLATE_SLOTS, primaryRankingMeasureFor } from '../template'
import { FieldLookup, offeredMeasures } from './withheld-measures'
import { DateWindow } from '../math/windows'
import { TopNotificationReportRow, topNotificationsContract } from './contracts'

/** Which of the template's two notification rankings is showing: its primary measure, or its rate behind a volume floor. */
export type NotificationRankingChoice = 'primary' | 'secondary'

export type RankingTable<Row> = { status: 'loading' | 'ready' | 'error'; rows: Row[] }

const NOTIFICATIONS_CANCELLATION_KEY = 'org-dashboard-top-notifications'

/**
 * The ranked notification table, read from the organization scope. `domainIds` is the dashboard's domain filter
 * (empty for every domain). In a mixed-currency e-commerce organization revenue cannot be ranked across domains,
 * so purchases take its place. The read asks only for the measures the organization's definitions offer, so it waits
 * for them, and is not sent (the table is ready and empty) when they do not offer the measure it ranks by or the one
 * its volume floor reads. The read is re-run when its inputs change or `retry` is called, and an in-flight
 * request is cancelled when it is superseded or the page goes away.
 */
export const useOrgDashboardRankings = (
    organizationId: number,
    range: DateWindow,
    domainIds: number[],
    template: DashboardTemplate,
    notificationRanking: NotificationRankingChoice,
    mixedCurrency: boolean,
    notificationCapabilities: FieldLookup | undefined,
) => {
    const [attempt, setAttempt] = useState(0)
    const [notifications, setNotifications] = useState<RankingTable<TopNotificationReportRow>>({
        status: 'loading',
        rows: [],
    })
    const { secondary, secondaryFloor } = TEMPLATE_SLOTS[template].rankings
    const primary = primaryRankingMeasureFor(template, mixedCurrency)

    useEffect(() => {
        if (!notificationCapabilities) return
        const rankBy = notificationRanking === 'primary' ? primary : secondary
        const floor = notificationRanking === 'primary' ? undefined : secondaryFloor
        const measuresTheRankingNeeds = [rankBy, ...(floor ? [floor.measure] : [])]
        const offered = offeredMeasures(notificationCapabilities, measuresTheRankingNeeds)
        if (offered.length < measuresTheRankingNeeds.length) {
            setNotifications({ status: 'ready', rows: [] })
            return
        }
        let cancelled = false
        setNotifications((previous) => ({ ...previous, status: 'loading' }))
        Container.get(InsightsService)
            .fetch(
                topNotificationsContract(
                    range,
                    domainIds,
                    rankBy,
                    floor,
                    offeredMeasures(notificationCapabilities, TEMPLATE_SLOTS[template].rankingMeasures),
                ),
                {
                    requestScope: 'organizations',
                    scopeId: organizationId,
                    cancellationKey: NOTIFICATIONS_CANCELLATION_KEY,
                },
            )
            .then((rows) => {
                if (cancelled) return
                setNotifications(rows ? { status: 'ready', rows } : { status: 'error', rows: [] })
            })
            .catch(() => {
                if (!cancelled) setNotifications((previous) => ({ ...previous, status: 'error' }))
            })
        return () => {
            cancelled = true
            cancelNamedAxiosRequest(NOTIFICATIONS_CANCELLATION_KEY)
        }
    }, [
        organizationId,
        notificationRanking,
        primary,
        range.since,
        range.through,
        domainIds.join(','),
        template,
        attempt,
        notificationCapabilities,
    ])

    return {
        notifications,
        status: notifications.status,
        retry: useCallback(() => setAttempt((count) => count + 1), []),
    }
}
